// Drift monitoring console wire contracts — the decision-console surface for the
// continuous-monitoring closed loop (catalog → activation → operating brief).
//
// These are the SHARED types between apps/api (initiative-operating-intelligence
// monitoring endpoints) and apps/web (decision console). The API services remain the
// behavioral source of truth; apps/api carries a compile-time conformance spec
// (drift-monitoring-console.contract.spec.ts) asserting its richer service types stay
// assignable to these wire contracts, so the two can never drift silently.
//
// Guardrail (same law as the drift advisory composer): these contracts carry ONLY
// structured facts, governed labels verbatim, codes, and evidence refs. No composed
// prose, no invented dollars. Narrative belongs to the governed Narrator exclusively.

import type { ObjectiveMetricIdentityUnresolvedReason } from './objective-metric-identity';

export const DRIFT_MONITORABLE_METRIC_CATALOG_SCHEMA_VERSION =
  'drift_monitorable_metric_catalog_v1' as const;
export const DRIFT_MONITORING_ACTIVATE_FROM_CATALOG_SCHEMA_VERSION =
  'drift_monitoring_activate_from_catalog_v1' as const;
export const DRIFT_MONITORING_ACTIVATE_FROM_CATALOG_RESULT_SCHEMA_VERSION =
  'drift_monitoring_activate_from_catalog_result_v1' as const;
export const DRIFT_OPERATING_BRIEF_SCHEMA_VERSION = 'drift_operating_brief_v1' as const;
export const DRIFT_CORRECTIVE_LEVER_RECOMMENDATIONS_SCHEMA_VERSION =
  'drift_corrective_lever_recommendations_v1' as const;

export type DriftMonitoringConsoleCadence = 'weekly' | 'monthly' | 'quarterly';

// --------------------------------------------------------------------------
// Catalog (GET monitoring/eligible-metrics)
// --------------------------------------------------------------------------

// Graph program Phase 4: the drift catalog joins the ONE metric-id namespace. The
// server resolves each objective's metricKey through the shared identity translation
// (resolveObjectiveMetricIdentity — canonical key or REGISTERED alias, version-scoped,
// never guessed) and RECORDS the outcome. An unresolved key is an honest, named state
// — it does not block the catalog row (a tenant may govern objectives ahead of metric
// registration), it makes the ungoverned identity visible instead of implied.
export type DriftGovernedMetricResolutionV1 =
  | Readonly<{
      status: 'resolved';
      canonicalMetricKey: string;
      registryVersion: string;
    }>
  | Readonly<{
      status: 'unresolved';
      // THE identity authority's own reason vocabulary (objective-metric-identity.ts)
      // — imported, never restated, so a reason added or renamed there cannot leave
      // this wire contract accepting a stale state.
      reason: ObjectiveMetricIdentityUnresolvedReason;
    }>;

export type DriftMonitorableMetricCatalogItemV1 = Readonly<{
  status: 'eligible' | 'blocked';
  objectiveType: string;
  label: string;
  metricKey: string;
  governedMetricResolution: DriftGovernedMetricResolutionV1;
  unit: string;
  direction: 'increase' | 'decrease';
  baselineValue: number;
  targetValue: number;
  baselineAsOf: string;
  targetAt: string;
  ownerUserId: string;
  cadence: string;
  blockedReasonCodes: readonly string[];
  evidenceRefs: readonly string[];
}>;

export type DriftMonitorableMetricCatalogV1 = Readonly<{
  schemaVersion: typeof DRIFT_MONITORABLE_METRIC_CATALOG_SCHEMA_VERSION;
  tenantId: string;
  strategyId: string;
  generatedAt: string;
  status: 'ok' | 'blocked';
  eligibleCount: number;
  blockedCount: number;
  metrics: readonly DriftMonitorableMetricCatalogItemV1[];
}>;

// --------------------------------------------------------------------------
// Activation (POST monitoring/activate-from-catalog)
// --------------------------------------------------------------------------

export type DriftMonitoringActivateFromCatalogRequestV1 = Readonly<{
  schemaVersion: typeof DRIFT_MONITORING_ACTIVATE_FROM_CATALOG_SCHEMA_VERSION;
  strategyId: string;
  title: string;
  metricKeys: readonly string[];
  approverUserIds?: readonly string[];
  cadence?: DriftMonitoringConsoleCadence;
  ownerUserId?: string;
  baselineAsOf?: string;
  activateAllEligible?: boolean;
}>;

/**
 * Subset of the ledger decision the console needs after activation. The API's
 * DecisionLedgerDecisionDto is richer; assignability is asserted API-side.
 */
export type DriftMonitoringConsoleActivationDecisionV1 = Readonly<{
  decisionId: string;
  status: string;
}>;

export type DriftMonitoringActivateFromCatalogResultV1 = Readonly<{
  schemaVersion: typeof DRIFT_MONITORING_ACTIVATE_FROM_CATALOG_RESULT_SCHEMA_VERSION;
  tenantId: string;
  strategyId: string;
  catalogGeneratedAt: string;
  selectedMetricKeys: readonly string[];
  activation: Readonly<{
    schemaVersion: 'drift_monitoring_activation_result_v1';
    scopeKind: string;
    scopeId: string;
    decision: DriftMonitoringConsoleActivationDecisionV1;
  }>;
}>;

// --------------------------------------------------------------------------
// Corrective-lever recommendations (deterministic, ledger-derived)
// --------------------------------------------------------------------------

/**
 * Where a corrective-lever recommendation comes from. Every basis is a governed,
 * already-written structure — nothing is inferred at recommendation time:
 * - accepted_plan_module: the drifting lever commitment's own decision module,
 *   recorded in its verification plan's leverPlanReference at acceptance time.
 * - accepted_plan_alternate: a sibling suggested lever recorded verbatim in the
 *   SAME accepted lever plan (the alternatives leadership saw when committing).
 * - strategy_lever_commitment: another ACTIVE lever commitment on the same
 *   strategy's Decision Ledger (for strategy-scope objective metrics).
 */
export type DriftCorrectiveLeverBasisV1 =
  | 'accepted_plan_module'
  | 'accepted_plan_alternate'
  | 'strategy_lever_commitment';

export type DriftCorrectiveLeverRecommendationV1 = Readonly<{
  basis: DriftCorrectiveLeverBasisV1;
  /** Lever identity recorded in the governed plan; null when the basis carries none. */
  leverId: string | null;
  leverFamilyId: string | null;
  /** Governed label carried VERBATIM from the ledger plan — never composed prose. */
  label: string;
  moduleId: string | null;
  /** Suggested-lever status recorded in the accepted plan, verbatim; null otherwise. */
  planStatus: string | null;
  /** Ledger decision this recommendation traces to. */
  decisionId: string;
  evidenceRefs: readonly string[];
}>;

export type DriftCorrectiveLeverGapCodeV1 = 'no_lever_reference' | 'ledger_unavailable';

export type DriftCorrectiveLeverSetV1 = Readonly<{
  decisionId: string;
  recommendations: readonly DriftCorrectiveLeverRecommendationV1[];
  /** Honest gap: set exactly when recommendations is empty, never alongside items. */
  gapCode: DriftCorrectiveLeverGapCodeV1 | null;
}>;

export type DriftCorrectiveLeverReadModelV1 = Readonly<{
  schemaVersion: typeof DRIFT_CORRECTIVE_LEVER_RECOMMENDATIONS_SCHEMA_VERSION;
  tenantId: string;
  strategyId: string | null;
  sets: readonly DriftCorrectiveLeverSetV1[];
}>;

// --------------------------------------------------------------------------
// Operating brief (GET monitoring/operating-brief)
// --------------------------------------------------------------------------

export type DriftOperatingBriefHeadlineStatusV1 =
  | 'blocked'
  | 'monitoring_gap'
  | 'decision_required'
  | 'attention_required'
  | 'on_track';

export type DriftOperatingBriefAlertV1 = Readonly<{
  alertId: string;
  severity: string;
  alertKind: string;
  actionRequired: boolean;
  decisionId: string;
  decisionTitle: string | null;
  ownerUserId: string | null;
  metricKey: string;
  label: string | null;
  unit: string | null;
  previousStatus: string | null;
  currentStatus: string | null;
  directionOfChange: 'worsened' | 'improved' | null;
  previousActualValue: number | null;
  currentActualValue: number | null;
  expectedValue: number | null;
  targetValue: number | null;
  sourceAsOf: string | null;
  detectedAt: string;
  sourceEvidenceRefs: readonly string[];
  targetEvidenceRef: string;
}>;

export type DriftOperatingBriefMetricV1 = Readonly<{
  decisionId: string;
  decisionTitle: string | null;
  scopeKind: string;
  scopeId: string;
  ownerUserId: string | null;
  planVersion: number;
  metricKey: string;
  label: string | null;
  unit: string | null;
  driftStatus: string;
  actualValue: number | null;
  expectedValue: number | null;
  targetValue: number | null;
  sourceAsOf: string | null;
  dataGapReasonCode: string | null;
  sourceEvidenceRefs: readonly string[];
  targetEvidenceRef: string;
  correctiveLevers: DriftCorrectiveLeverSetV1 | null;
}>;

export type DriftOperatingBriefV1 = Readonly<{
  schemaVersion: typeof DRIFT_OPERATING_BRIEF_SCHEMA_VERSION;
  tenantId: string;
  strategyId: string | null;
  generatedAt: string;
  status: 'ok' | 'blocked';
  blockedReasonCodes: readonly string[];
  headlineStatus: DriftOperatingBriefHeadlineStatusV1;
  executiveTakeaway: string;
  rollup: Readonly<{
    tracked: number;
    exceptions: number;
    actionRequired: number;
    criticalAlerts: number;
    warningAlerts: number;
    dataGaps: number;
  }>;
  topAlerts: readonly DriftOperatingBriefAlertV1[];
  metricsNeedingAttention: readonly DriftOperatingBriefMetricV1[];
  ownerUserIds: readonly string[];
  recommendedActionCodes: readonly string[];
  evidenceRefs: readonly string[];
}>;
